import { format, formatDistanceToNow } from "date-fns";
import { hu } from "date-fns/locale";
import type { Address } from "./types";

export const fmtDate = (d: string | Date | null | undefined) => (d ? format(new Date(d), "yyyy. MM. dd.", { locale: hu }) : "–");
export const fmtDateTime = (d: string | Date | null | undefined) => (d ? format(new Date(d), "yyyy. MM. dd. HH:mm", { locale: hu }) : "–");
export const fmtAgo = (d: string | Date | null | undefined) => (d ? formatDistanceToNow(new Date(d), { locale: hu, addSuffix: true }) : "soha");
export const fmtAddress = (a: Pick<Address, "zip" | "city" | "street">) => `${a.zip} ${a.city}, ${a.street}`;

/** Napszaknak megfelelő köszönés. */
export function greeting(name?: string | null) {
  const h = new Date().getHours();
  const first = name?.split(" ").slice(-1)[0];
  const base = h < 5 ? "Szép estét" : h < 10 ? "Jó reggelt" : h < 18 ? "Szép napot" : "Szép estét";
  return first ? `${base}, ${first}!` : `${base}!`;
}

const areaFmt = new Intl.NumberFormat("hu-HU", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const pctFmt = new Intl.NumberFormat("hu-HU", { style: "percent", maximumFractionDigits: 1 });

export const fmtArea = (n: number | null | undefined) => `${areaFmt.format(n ?? 0)} m²`;
export const fmtNum = (n: number | null | undefined) => areaFmt.format(n ?? 0);
export const fmtPct = (n: number) => pctFmt.format(n);

/** "2026-03-01" → "2026. 03. 01." */
export const fmtIso = (iso: string | null | undefined) => (iso ? iso.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$1. $2. $3.") : "–");

const HU_MONTH = ["január", "február", "március", "április", "május", "június", "július", "augusztus", "szeptember", "október", "november", "december"];

/**
 * Időszak két dátumból. Teljes naptári hónap / év esetén a beszédes alak („2026. augusztus”),
 * különben teljes dátumok en dash-sel. Dátumot nem toldalékolunk (pl. „01.-tól”), mert csúnya és hibás.
 */
export function fmtPeriod(from: string | null | undefined, to: string | null | undefined): string {
  if (!from) return to ? `– ${fmtIso(to)}` : "–";
  if (!to) return `${fmtIso(from)} napjától`;
  if (from === to) return fmtIso(from);
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  const lastDay = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  if (fd === 1 && td === lastDay) {
    if (fy === ty && fm === tm) return `${fy}. ${HU_MONTH[fm - 1]}`;
    if (fy === ty && fm === 1 && tm === 12) return `${fy}. év`;
  }
  return `${fmtIso(from)} – ${fmtIso(to)}`;
}

/** „2026. 01. 01. napjától” – toldalék nélküli, nyitott kezdet. */
export const fmtFromDay = (iso: string | null | undefined) => (iso ? `${fmtIso(iso)} napjától` : "–");
/** „2026. 12. 31. napjáig” – toldalék nélküli, nyitott vég. */
export const fmtToDay = (iso: string | null | undefined) => (iso ? `${fmtIso(iso)} napjáig` : "–");

const moneyFmt = (cur: string, digits = 0) => new Intl.NumberFormat("hu-HU", { style: "currency", currency: cur, maximumFractionDigits: digits, minimumFractionDigits: 0 });

/** Pénzösszeg devizával: 1 234 567 Ft, 1 234,50 € */
export const fmtMoney = (n: number | null | undefined, cur = "HUF") => (n == null ? "–" : moneyFmt(cur, cur === "HUF" ? 0 : 2).format(n));

export function fmtRemaining(r: { years: number; months: number; days: number } | null | undefined) {
  if (!r) return "határozatlan";
  const parts = [r.years ? `${r.years} év` : "", r.months ? `${r.months} hó` : "", r.days && !r.years ? `${r.days} nap` : ""].filter(Boolean);
  return parts.length ? parts.join(" ") : "lejárt";
}

export function daysUntil(iso: string) {
  const t = new Date();
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - today) / 86_400_000);
}
